import React, { useState, useEffect } from "react";
import SuccessOrder from "../../components/SuccessOrder";
import SuccessPopup from "../../components/SuccessOrder";
import ShippingAddressForm from "../../components/ShippingForm";
import { p } from "framer-motion/client";
import { useCartContext } from "../../components/CartContext";
import api from "../../api/axiosClient";
import { useAuth } from "../../context/AuthContext";

function GlassTile({ className = "", children }) {
  return (
    <div
      className={
        "bg-white/40 backdrop-blur-xl border border-white/25 rounded-2xl shadow-lg " +
        className
      }
      style={{ WebkitBackdropFilter: "blur(14px) saturate(140%)" }}
    >
      {children}
    </div>
  );
}

const Checkout = () => {
  // 👇 Get cart + helpers from shared hook
  const {
    cart,
    subtotal,
    shipping,
    taxes,
    total,
    clearCart
  } = useCartContext();
  const [toast, setToast] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [address, setAddress] = useState({
    first_name: "",
    last_name: "",
    address: "",
    phone: "",
    email: "",
    city: "",
    zip: "",
    province: "",
    country_code: "IN",
  });
  const [isAddressValid, setIsAddressValid] = useState(false);
  const { accessToken, user } = useAuth();
  const token = accessToken;
  const userId = user?.userId || user?.id;
  const handleChange = (e) => {
    setAddress({ ...address, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    console.log("Address valid:", isAddressValid);
  }, [isAddressValid]);


  const handlePay = async () => {
    try {
      if (cart.length === 0) {
        alert("Cart is empty");
        return;
      }

      // 1️⃣ Create backend order
      const res = await fetch("/api/payments/create-order", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          userId,
          amount: total,               // ✅ from useCart
          currency: "INR",
          address: {
            first_name: address.first_name,
            last_name: address.last_name,
            address1: address.address,
            address2: "",
            phone: address.phone,
            email: address.email,
            city: address.city,
            zip: address.zip,
            province: address.province,
            country_code: address.country_code,
          },
          cartItems: cart.map(item => ({
            productId: item.variantId,
            name: item.productName,
            quantity: item.quantity,
            price: item.price,
          })),
        }),
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText);
      }

      const orderData = await res.json();

      // 2️⃣ Razorpay options
      const options = {
        key: orderData.keyId,
        amount: orderData.amount,
        currency: orderData.currency,
        name: "5Buddies Store",
        description: "Checkout Payment",
        order_id: orderData.razorpayOrderId,

        handler: async function (response) {
          // 3️⃣ Verify payment
          const verifyRes = await fetch("/api/payments/verify", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify({
              ...response,
              backendOrderId: orderData.backendOrderId,
              userId,
              orderId: orderData.orderId,
              productId: orderData.productId,
              cartItems: cart.map(item => ({
                variantId: item.variantId,
                productName: item.productName,
                quantity: item.quantity,
                price: item.price,
              })),
              address: {
                first_name: address.first_name,
                last_name: address.last_name,
                address1: address.address,
                address2: "",
                phone: address.phone,
                email: address.email,
                city: address.city,
                zip: address.zip,
                province: address.province,
                country_code: address.country_code,
              },
            }),
          });

          const verifyResult = await verifyRes.json();

          if (verifyResult.success) {
            // 4️⃣ Push to Qikink
            await fetch(
              `/api/order/confirm?orderId=${orderData.backendOrderId}`,
              { method: "POST" }
            );

            // 5️⃣ Remove cart items from backend using variantId
            try {
              for (const item of cart) {
                await api.post("/cart/remove", {
                  variantId: item.variantId
                });
              }
              console.log("Cart items removed from backend");
            } catch (err) {
              console.error("Error removing cart items:", err);
            }

            // 6️⃣ Clear cart ONLY after success
            clearCart();
            console.log("Cart cleared after successful payment");
            setShowSuccess(true);
            console.log("Payment successful and verified");
          } else {
            alert("Payment verification failed");
          }
        },

        prefill: {
          name: address.fullName,
          email: "test@example.com",
          contact: "9876543210",
        },

        notes: {
          address: `${address.street}, ${address.city}, ${address.zip}`,
        },

        theme: { color: "#2563EB" },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();

    } catch (err) {
      console.error("Checkout error:", err);
      alert("Payment initialization failed");
    }
  };





  return (
    <div className="min-h-screen overflow-y-auto">

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 pt-28 pb-20">

        <h1 className="text-3xl font-bold text-gray-800 mb-10">Checkout</h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">

          {/* 🛒 Order Summary */}
          <GlassTile className="p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Order Summary
            </h2>

            <div className="space-y-5">

              {cart.map(item => (
                <div key={item.productId} className="flex justify-between items-center">
                  <div>
                    <p className="font-medium text-gray-800">
                      {item.productName}
                    </p>
                    <p className="text-sm text-gray-600">
                      Qty: {item.quantity}
                    </p>
                  </div>
                  <p className="text-gray-800 font-semibold">
                    ₹{item.price * item.quantity}
                  </p>
                </div>
              ))}

              {/* Price Summary */}
              <div className="border-t border-white/40 pt-4 space-y-2">
                <div className="flex justify-between text-gray-700">
                  <span>Shipping</span>
                  <span>₹{shipping}</span>
                </div>
                <div className="flex justify-between text-gray-700">
                  <span>Taxes</span>
                  <span>₹{taxes}</span>
                </div>
                <div className="flex justify-between text-gray-800 font-semibold text-lg pt-2">
                  <span>Total</span>
                  <span>₹{total}</span>
                </div>
              </div>

            </div>
          </GlassTile>

          {/* 📦 Shipping + Payment */}
          <GlassTile className="p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Shipping Address
            </h2>

            <ShippingAddressForm
              address={address}
              setAddress={setAddress}
              onValidityChange={setIsAddressValid}
            />


            {/* Payment Section */}
            <div className="mt-8">
              <h2 className="text-lg font-semibold text-gray-800 mb-3">
                Payment
              </h2>

              <button
                onClick={handlePay}
                disabled={!isAddressValid}
                className={`
                    w-full text-white font-semibold text-lg
                    px-6 py-3 rounded-xl transition
                    ${isAddressValid
                    ? "bg-blue-600 hover:bg-blue-700"
                    : "bg-gray-400 cursor-not-allowed"}
                  `}
              >
                Pay Now ₹{total}
              </button>

            </div>

          </GlassTile>

        </div>
      </main>

      <footer className="bg-white/30 backdrop-blur-lg border-t border-white/40 py-4 text-center text-gray-600">
        © 2025 5Buddies Checkout
      </footer>

      <SuccessPopup open={showSuccess} onClose={() => setShowSuccess(false)} />
      {/* ⭐ Render Toast HERE */}
      {toast && <Toast message={toast} onClose={() => setToast("")} />}
    </div>
  );
};

export default Checkout;
